import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import {
  AuthenticatedIdentity,
  AuthenticationService,
  LogoutOption,
} from '@ups/security';
import { EventBusService } from '@ups/xplat/core';
import {
  LoginIssuer,
  LoginIssuerStore,
  accountCountSafe,
  selectLoginIssuer,
} from './login-issuer.store';

/**
 * Single multi-issuer session surface for web apps (#29709): which issuer
 * the current session rides, who is signed in, and a token for API calls.
 * Token refresh stays with the owning lib — the bound
 * {@link AuthenticationService} delegates to MSAL `acquireTokenSilent` or
 * Auth0's cache/refresh under the hood, this facade never mints tokens
 * itself. Issuer selection runs once per boot (redirect logins reboot the
 * app) with the same rule the AuthenticationService binding uses; an app
 * without MSAL registered is always 'auth0', whatever localStorage says.
 */
@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly auth = inject(AuthenticationService);
  private readonly issuerStore = inject(LoginIssuerStore);
  private readonly eventBus = inject(EventBusService);
  private readonly msal = inject(MsalService, { optional: true });

  // accountCountSafe, not a bare getAllAccounts (WI 31092): this field
  // initializer runs pre-initialize, where msal-browser v5 throws on account
  // reads once the persisted localStorage cache holds accounts (returning
  // boots). The persisted marker alone selects 'entra' for those sessions.
  readonly issuer: LoginIssuer = this.msal
    ? selectLoginIssuer(
        this.issuerStore.get(),
        accountCountSafe(this.msal.instance)
      )
    : 'auth0';

  readonly identity$: Observable<AuthenticatedIdentity | null> =
    this.auth.identity$;
  readonly isAuthenticated$: Observable<boolean> = this.auth.isAuthenticated$;
  /**
   * Raw ID-token claims, issuer-agnostic (WI 34796, PR 4337 review #52493):
   * `Auth0AuthenticationService` binds `auth0.idTokenClaims$`,
   * `EntraAuthenticationService` binds MSAL's active-account
   * `idTokenClaims`. `identity$` alone does not carry a display name for
   * either issuer, and carries no email at all for Auth0 (`{kind: 'auth0',
   * sub}`) — a consumer needing `name`/`email` (e.g. My day's identity) reads
   * both streams together rather than reaching past the facade for Auth0's
   * `AuthService.user$` directly, which would stop following whichever
   * issuer the session actually rides.
   */
  readonly claims$: Observable<Record<string, unknown> | undefined | null> =
    this.auth.claims$;

  getAccessToken(forceRefresh = false): Observable<string> {
    return this.auth.getAccessTokenSilently(forceRefresh);
  }

  logout(options: LogoutOption): void {
    // Emit before IdP redirect so Ryn Replay can anonymize + keepalive-flush
    // while the document is still alive (#30073 AC5).
    this.eventBus.emit(this.eventBus.types.logout);
    this.auth.logout(options);
  }
}
